import React, { FC, useRef, useState, useEffect, ReactNode, useMemo } from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import PdImage from '@components/pd-image/pd-image'
import { CustomerCase } from '@service/customer-new.service'
import Image from 'next/image'

const CaseCard: FC<Partial<CustomerCase & { mini?: boolean; boxClassName?: string }>> = ({
  title,
  country,
  industryName,
  productName = [],
  mediaUrls,
  mini,
  boxClassName,
  docId,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const childRefs = useRef<Array<HTMLSpanElement | null>>([])
  const [overflowIndex, setOverflowIndex] = useState(-1)

  // 计算溢出的索引
  const calculateOverflow = () => {
    if (!parentRef.current || childRefs.current.length === 0) return

    const parentWidth = parentRef.current.offsetWidth
    const parentStyles = !!window && window.getComputedStyle(parentRef.current)
    const gap = parseFloat(parentStyles.gap) || 0
    let totalWidth = 0

    for (let i = 0; i < childRefs.current.length; i++) {
      const child = childRefs.current[i]

      if (!child) break
      totalWidth += child.offsetWidth + (i > 0 ? gap : 0)
      if (totalWidth + (window?.innerWidth < 1024 ? 24 : 64) > parentWidth) break

      setOverflowIndex(i)
    }
  }

  // 初始化监听父容器和子元素尺寸变化
  useEffect(() => {
    const parent = parentRef.current
    if (!parent) return

    // 初始计算
    calculateOverflow()
  }, [calculateOverflow])

  const imageUrl = useMemo(
    () => mediaUrls?.find((url) => ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(url.split('.').pop() || '')) || '',
    [mediaUrls]
  )

  const showChildDoms = useMemo<ReactNode[]>(() => {
    const shouldShowProductName: string[] = productName.slice(0, overflowIndex + 1)

    return shouldShowProductName?.map((tag, index) => (
      <span
        key={index}
        className="border px-3 py-0.5 rounded-sm outline-1 outline-offset-[-1px] outline-zinc-300 justify-start items-center gap-2"
      >
        {tag}
      </span>
    ))
  }, [overflowIndex, productName])

  const cleanDocId = useMemo(() => {
    return docId?.replace(/[\u200B-\u200D\uFEFF]/g, '')
  }, [docId])

  return (
    <Link href={`/case-studies/${cleanDocId}`}>
      <div className={clsx('w-full h-full bg-white flex flex-col group relative cursor-pointer', boxClassName)}>
        <div className={clsx('w-full', mini ? 'h-192px laptop:h-293px' : 'laptop:h-395px h-218px')}>
          <PdImage
            src={imageUrl}
            alt={title}
            object="cover"
            objectPosition="center"
            enableMask={false}
            width="100%"
            height="100%"
          />
        </div>
        <div
          className={clsx(
            'w-full flex flex-col p-3',
            mini ? 'h-180px laptop:h-196px' : 'h-180px laptop:h-52',
            'laptop:p-6'
          )}
        >
          <h3 className="text-black text-base laptop:text-xl font-medium mb-1 laptop:mb-2 line-clamp-2">{title}</h3>
          <div className="flex items-center gap-x-4 text-black/60 text-sm font-normal leading-snug">
            <span>{country}</span>
            {country && industryName && <span className="bg-black/60 w-px h-4" />}
            <span className="break-words line-clamp-3">{industryName}</span>
          </div>
          <div ref={parentRef} className="mt-auto flex flex-wrap gap-2 text-black/60 text-sm font-normal leading-snug">
            {overflowIndex === -1 &&
              productName?.map((tag, index) => (
                <span
                  key={index}
                  ref={(el) => (childRefs.current[index] = el)}
                  className="border px-3 py-0.5 rounded-sm outline-1 outline-offset-[-1px] outline-zinc-300 justify-start items-center gap-2"
                >
                  {tag}
                </span>
              ))}
            {overflowIndex !== -1 && showChildDoms}
            {productName.length !== overflowIndex + 1 && (
              <span className="flex items-center">
                <span className="w-0.5 h-0.5 bg-black/60 rounded-full" />
                <span className="w-0.5 h-0.5 bg-black/60 rounded-full mx-0.5" />
                <span className="w-0.5 h-0.5 bg-black/60 rounded-full" />
              </span>
            )}
          </div>
        </div>
        <div className="w-6 h-6 absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <Image src="/icons/icon_indicator.svg" alt="indicator" layout="fill" />
        </div>
      </div>
    </Link>
  )
}

export default CaseCard
